import { useState, useEffect, useRef } from "react";
import { getPendingSave, clearPendingSave } from "@/lib/pendingSave";
import { Switch, Route, Router as WouterRouter, useLocation, useSearch } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useToggleQuoteFavorite } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { UserProfileProvider, useUserProfile } from "@/contexts/UserProfileContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PageHeader } from "@/components/PageHeader";
import { Sidebar } from "@/components/Sidebar";
import { MobileNav } from "@/components/MobileNav";
import { OnboardingModal } from "@/components/OnboardingModal";
import { FeedbackButton } from "@/components/FeedbackButton";
import { isAdmin } from "@/lib/admin";
import { navRoutes } from "@/lib/nav-routes";

import { CommunityPage } from "@/pages/CommunityPage";
import { LandingPage } from "@/pages/LandingPage";
import { DailyPage } from "@/pages/DailyPage";
import { QuoteGridPage } from "@/pages/QuoteGridPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { HarvestPage } from "@/pages/HarvestPage";
import { GovernancePage } from "@/pages/GovernancePage";
import { EntityResolutionPage } from "@/pages/EntityResolutionPage";
import { AuthorPage } from "@/pages/AuthorPage";
import { TagPage } from "@/pages/TagPage";
import { QuotePage } from "@/pages/QuotePage";
import { SearchPage } from "@/pages/SearchPage";
import { SignInPage } from "@/pages/SignInPage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { TermsPage } from "@/pages/TermsPage";
import NotFound from "@/pages/not-found";

import { queryClient } from "@/lib/queryClient";
import { COMMUNITY_FEED_ENABLED } from "@/lib/community-launch";

const GRID_NAVS = ["Library", "Favorites", "Discover"];

/** Routes accessible without signing in (read-only browsing). */
const PUBLIC_PATHS = ["/community"];

function AccessDenied() {
  const [, navigate] = useLocation();
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
      <p className="text-lg font-semibold text-stone-900 mb-2">Access restricted</p>
      <p className="text-sm text-stone-500 mb-6">This page is for admins only.</p>
      <button
        onClick={() => navigate("/")}
        className="rounded-2xl bg-stone-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-stone-700 transition"
      >
        Go home
      </button>
    </div>
  );
}

/**
 * Fires the pending-save favorite mutation once after the user signs in or
 * signs up from the LandingPage "Save this quote" guest-save flow.
 * Mounts only when currentUser is set (inside the authenticated shell).
 */
function PendingSaveProcessor() {
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const { toast } = useToast();
  const processedRef = useRef(false);

  const { mutate: toggleFavorite } = useToggleQuoteFavorite({
    mutation: {
      onSuccess: () => {
        clearPendingSave();
        processedRef.current = false;
        toast({
          title: "Quote saved!",
          description: "Check your Favorites tab to find it.",
        });
      },
      onError: () => {
        // Leave the pending save in place so the user can retry manually.
        processedRef.current = false;
      },
    },
  });

  useEffect(() => {
    if (isAuthLoading || !currentUser || processedRef.current) return;
    const pending = getPendingSave();
    if (!pending) return;
    processedRef.current = true;
    toggleFavorite({ id: pending.quoteId });
  }, [currentUser, isAuthLoading, toggleFavorite]);

  return null;
}

function AppLayout() {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const { isLoading: isProfileLoading, hasCompletedOnboarding } = useUserProfile();

  const isPublicRoute = PUBLIC_PATHS.some(
    (p) => location === p || location.startsWith(p + "/"),
  );

  const [inputValue, setInputValue] = useState("");
  // Remember the last non-search tab so Clear can restore it
  const prevLocationRef = useRef<string>("/");

  const activeNav =
    location === "/search"
      ? "Search"
      : location.startsWith("/authors/") || location.startsWith("/tags/")
        ? "Library"
        : navRoutes.find((r) => r.path !== "/" && location.startsWith(r.path))?.label ??
          (location === "/" ? "Daily" : "Daily");

  // Sync input value and track prev-location whenever the route or URL query changes.
  // Both `location` and `search` are included so back/forward between different
  // /search?q=... entries also updates the input.
  useEffect(() => {
    if (location.startsWith("/search")) {
      const q = new URLSearchParams(search).get("q") ?? "";
      setInputValue(q);
    } else {
      prevLocationRef.current = location;
      setInputValue("");
    }
  }, [location, search]);

  // Debounced navigation for non-grid tabs (Daily, Profile, Harvest)
  useEffect(() => {
    if (GRID_NAVS.includes(activeNav)) return;
    if (!inputValue.trim()) return;
    const timer = setTimeout(() => {
      const targetUrl = `/search?q=${encodeURIComponent(inputValue)}`;
      // Replace the current history entry when already on /search to avoid
      // accumulating a separate history entry for every debounce tick.
      navigate(targetUrl, { replace: location.startsWith("/search") });
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, activeNav, navigate, location]);

  function handleSearch(value: string) {
    setInputValue(value);
    // When the user clears the input on /search, restore the previous tab
    if (!value.trim() && location.startsWith("/search")) {
      navigate(prevLocationRef.current || "/");
    }
  }

  // Enter key: bypass the debounce and navigate immediately
  function handleEnterSubmit() {
    if (!GRID_NAVS.includes(activeNav) && inputValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(inputValue)}`);
    }
  }

  if (isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-stone-900" />
      </div>
    );
  }

  if (!currentUser && !isPublicRoute) {
    if (location === "/") {
      return (
        <>
          <LandingPage />
          <FeedbackButton variant="mobile-fab" />
        </>
      );
    }
    return (
      <>
        <SignInPage />
        <FeedbackButton variant="mobile-fab" />
      </>
    );
  }

  if (currentUser && isProfileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-stone-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {currentUser && !hasCompletedOnboarding && <OnboardingModal />}
      <PendingSaveProcessor />
      <MobileNav activeNav={activeNav} />
      <div className="mx-auto flex max-w-[1280px] gap-6 px-4 py-5 pb-nav-safe md:px-6 lg:px-8 lg:pb-5">
        <Sidebar activeNav={activeNav} />
        <main className="min-w-0 flex-1">
          <PageHeader
            activeNav={activeNav}
            searchValue={inputValue}
            onSearch={handleSearch}
            onEnter={handleEnterSubmit}
            hideSearch={activeNav === "Profile"}
            subtitle={
              {
                Library: "Search the entire database for quotes!",
                Community: COMMUNITY_FEED_ENABLED
                  ? "Browse and vote on reader-submitted quotes"
                  : "Coming soon — help us get started by submitting a quote",
                Favorites: "Quotes you've favorited, these help build your quote profile!",
                Discover: "Quotes specifically pulled for you that align with your quote profile!",
                Profile: "Pin and submit quotes, view your activity, and opt in to receive daily/weekly quotes by email!",
                Harvest: "Review and approve incoming quote candidates",
                Search: "Search the entire database for quotes",
              }[activeNav]
            }
          />
          <Switch>
            <Route path="/" component={DailyPage} />
            <Route path="/library">
              <QuoteGridPage activeNav="Library" searchQuery={inputValue} />
            </Route>
            <Route path="/community" component={CommunityPage} />
            <Route path="/favorites">
              <QuoteGridPage activeNav="Favorites" searchQuery={inputValue} />
            </Route>
            <Route path="/discover">
              <QuoteGridPage activeNav="Discover" searchQuery={inputValue} />
            </Route>
            <Route path="/profile" component={ProfilePage} />
            <Route path="/harvest/governance">
              {isAdmin(currentUser?.uid) ? <GovernancePage /> : <AccessDenied />}
            </Route>
            <Route path="/harvest/resolution">
              {isAdmin(currentUser?.uid) ? <EntityResolutionPage /> : <AccessDenied />}
            </Route>
            <Route path="/harvest">
              {isAdmin(currentUser?.uid) ? <HarvestPage /> : <AccessDenied />}
            </Route>
            <Route path="/search">
              <SearchPage searchQuery={inputValue} />
            </Route>
            <Route path="/authors/:name" component={AuthorPage} />
            <Route path="/tags/:name" component={TagPage} />
            <Route component={NotFound} />
          </Switch>
        </main>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const campaign = params.get("campaign");
    let changed = false;
    if (ref) {
      localStorage.setItem("referral_ref", ref);
      params.delete("ref");
      changed = true;
    }
    if (campaign) {
      localStorage.setItem("referral_campaign", campaign);
      params.delete("campaign");
      changed = true;
    }
    if (changed) {
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : "") +
        window.location.hash;
      history.replaceState(null, "", newUrl);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ThemeProvider>
          <AuthProvider>
            <UserProfileProvider>
              <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                <Switch>
                  {/* Public routes — no auth required. Each gets a floating
                      Feedback button so anonymous visitors can still submit. */}
                  <Route path="/quotes/:id">
                    <QuotePage />
                    <FeedbackButton variant="mobile-fab" />
                  </Route>
                  <Route path="/privacy">
                    <PrivacyPage />
                    <FeedbackButton variant="mobile-fab" />
                  </Route>
                  <Route path="/terms">
                    <TermsPage />
                    <FeedbackButton variant="mobile-fab" />
                  </Route>
                  {/* All other routes go through the authenticated shell */}
                  <Route component={AppLayout} />
                </Switch>
              </WouterRouter>
            </UserProfileProvider>
          </AuthProvider>
        </ThemeProvider>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
